import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  message, 
  Space,
  Typography,
  Alert,
  Row,
  Col
} from 'antd';
import { 
  LockOutlined, 
  SafetyOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import lib from '../../../assets/utils/lib';

const { Title, Text } = Typography;
const { Password } = Input;

const PasswordManagementModal = ({ organization, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const handleSubmit = async (values) => {
    if (!organization?.org_social_no) {
      messageApi.error('Organization social number is required');
      return;
    }

    setLoading(true);
    try {
      // Use the hash method for better security
      const hashedSocialNo = btoa(organization.org_social_no);
      const response = await lib.setOrganizationPasswordByHash(hashedSocialNo, values.password);
      
      if (response?.data?.error === 0) {
        messageApi.success('Password updated successfully');
        form.resetFields();
        onClose();
      } else {
        messageApi.error(response?.data?.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Password update error:', error);
      messageApi.error('Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject('Please enter a password');
    }
    if (value.length < 6) {
      return Promise.reject('Password must be at least 6 characters long');
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = (_, value) => {
    if (!value) {
      return Promise.reject('Please confirm your password');
    }
    if (value !== form.getFieldValue('password')) {
      return Promise.reject('Passwords do not match');
    }
    return Promise.resolve();
  };

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Organization Info */}
        <Alert
          message="Organization Details"
          description={
            <div>
              <div><strong>Name:</strong> {organization?.org_name}</div>
              <div><strong>Org code:</strong> {organization?.org_social_no}</div>
              <div><strong>State:</strong> {organization?.org_state}</div>
            </div>
          }
          type="info"
          showIcon
          icon={<CheckCircleOutlined />}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="New Password"
                rules={[{ validator: validatePassword }]}
                tooltip="Password must be at least 6 characters long"
              >
                <Password
                  placeholder="Enter new password"
                  prefix={<LockOutlined />}
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                rules={[{ validator: validateConfirmPassword }]}
                tooltip="Re-enter the password to confirm"
              >
                <Password
                  placeholder="Confirm new password"
                  prefix={<LockOutlined />}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: '24px' }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                icon={<SafetyOutlined />}
                style={{ minWidth: '150px' }}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
              <Button
                onClick={onClose}
                size="large"
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Alert
          message="Security Notice"
          description="Password changes take effect immediately. The organization will need to use the new password for their next login."
          type="warning"
          showIcon
          style={{ marginTop: '16px' }}
        />
      </Space>
    </>
  );
};

export default PasswordManagementModal;

